import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Briefcase, CaretDown, CaretRight, Users, Star, Coins, TrendUp } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("de-DE").format(Math.round(n || 0));

const JOB_COLORS = {
  lspd: "text-blue-400", police: "text-blue-400", sheriff: "text-blue-400",
  ambulance: "text-red-400", ems: "text-red-400", doctor: "text-red-400",
  mechanic: "text-orange-400", taxi: "text-yellow-400",
  admin: "text-volt", unemployed: "text-white/40",
};

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/jobs")
      .then(r => setJobs(r.data))
      .catch(() => toast.error("Jobs konnten nicht geladen werden"))
      .finally(() => setLoading(false));
  }, []);

  const totalMembers = jobs.reduce((a, b) => a + (b.count || 0), 0);
  const totalWealth = jobs.reduce((a, b) => a + (b.total_cash || 0) + (b.total_bank || 0), 0);

  return (
    <div>
      <PageHeader
        eyebrow="07 // FRAKTIONEN"
        title="Fraktionsübersicht"
        description="Alle Jobs deines Servers mit Rängen, Vermögen und Mitgliedern — Live aus der ESX Datenbank."
        right={
          <div className="font-mono text-right">
            <div className="text-[10px] text-white/40 uppercase tracking-widest">Fraktionen · Mitglieder</div>
            <div className="text-2xl font-black">{jobs.length} <span className="text-volt">· {totalMembers}</span></div>
          </div>
        }
      />
      <div className="p-4 md:p-8 lg:p-12 space-y-4" data-testid="jobs-page">
        {loading && <div className="font-mono text-white/40 text-sm">Lade Jobs...</div>}

        {!loading && jobs.length === 0 && (
          <div className="border border-tactical-border border-dashed p-12 text-center font-mono text-white/40">
            Keine Jobs gefunden. Führe im Admin-Panel zuerst "Jetzt Syncen" aus.
          </div>
        )}

        <div className="grid gap-3 stagger">
          {jobs.map(j => {
            const isOpen = expanded[j.job];
            const totalWealthJob = (j.total_cash || 0) + (j.total_bank || 0);
            const jobColor = JOB_COLORS[j.job] || "text-white";
            return (
              <div key={j.job} className="border border-tactical-border bg-tactical-surface" data-testid={`job-${j.job}`}>
                <button
                  onClick={() => setExpanded({ ...expanded, [j.job]: !isOpen })}
                  className="w-full p-4 md:p-5 flex items-center gap-4 hover:bg-tactical-elev transition-colors text-left"
                >
                  <div className={`w-11 h-11 border border-tactical-border bg-black flex items-center justify-center flex-shrink-0 ${jobColor}`}>
                    <Briefcase size={20} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`font-display font-black text-xl uppercase tracking-wider ${jobColor}`}>{j.job}</div>
                    <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest mt-0.5">
                      <Users size={10} className="inline mr-1" />{j.count} Mitglieder ·
                      Ø Level {Math.round(j.avg_level || 0)}
                    </div>
                  </div>
                  <div className="hidden md:grid grid-cols-3 gap-6 font-mono text-right">
                    <div>
                      <div className="text-[9px] text-white/40 uppercase tracking-widest">Cash Σ</div>
                      <div className="text-tactical-success text-sm">${fmt(j.total_cash)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] text-white/40 uppercase tracking-widest">Bank Σ</div>
                      <div className="text-volt text-sm">${fmt(j.total_bank)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] text-white/40 uppercase tracking-widest">Vermögen</div>
                      <div className="text-white text-sm">${fmt(totalWealthJob)}</div>
                    </div>
                  </div>
                  {isOpen ? <CaretDown size={16} className="text-white/50 flex-shrink-0" /> : <CaretRight size={16} className="text-white/50 flex-shrink-0" />}
                </button>

                {isOpen && (
                  <div className="border-t border-tactical-border">
                    <div className="grid grid-cols-12 gap-2 px-4 py-2 font-mono text-[10px] text-white/40 uppercase tracking-widest bg-black/30">
                      <div className="col-span-5 md:col-span-4">Mitglied</div>
                      <div className="col-span-4 md:col-span-3">Rang</div>
                      <div className="col-span-1 text-right">Lvl</div>
                      <div className="hidden md:block col-span-2 text-right">Cash</div>
                      <div className="col-span-2 text-right">Bank</div>
                    </div>
                    <div className="divide-y divide-tactical-border/50 max-h-96 overflow-y-auto">
                      {(j.members || []).sort((a, b) => (b.bank || 0) - (a.bank || 0)).map((m, i) => (
                        <div key={m.id || i} className="grid grid-cols-12 gap-2 px-4 py-2 items-center font-mono text-sm hover:bg-tactical-elev/50 transition-colors" data-testid={`job-${j.job}-member-${i}`}>
                          <div className="col-span-5 md:col-span-4 text-white truncate">
                            <Star size={11} className="inline text-white/30 mr-1" />
                            {m.first_name} {m.last_name}
                          </div>
                          <div className="col-span-4 md:col-span-3 text-white/70 truncate">{m.grade_label || "—"}</div>
                          <div className="col-span-1 text-right text-white/60">{m.level || 1}</div>
                          <div className="hidden md:block col-span-2 text-right text-tactical-success">${fmt(m.cash)}</div>
                          <div className="col-span-2 text-right text-volt">${fmt(m.bank)}</div>
                        </div>
                      ))}
                    </div>
                    {j.total_salary > 0 && (
                      <div className="px-4 py-2 border-t border-tactical-border/50 font-mono text-xs text-white/50">
                        <Coins size={11} className="inline text-tactical-warn mr-1" />
                        Gehaltsvolumen pro Zyklus: <span className="text-tactical-warn">${fmt(j.total_salary)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {jobs.length > 0 && (
          <div className="mt-6 border border-tactical-border p-4 font-mono text-xs text-white/50 flex items-center gap-3">
            <TrendUp size={14} className="text-volt" />
            Fraktions-Gesamtvermögen: <span className="text-volt">${fmt(totalWealth)}</span> · {totalMembers} Mitglieder aktiv
          </div>
        )}
      </div>
    </div>
  );
}
